import {
  ProtobufAxiosRequestConfig,
  ProtobufHttpClient,
  REQUEST_CONFIG_NAMESPACE,
} from "@ftdr/http-utils";
import { MicroService } from "@ftdr/micro-services-js-clients-shared";
import { AxiosResponse } from "axios";
import { retry } from "ts-retry-promise";
import {
  contractpb,
  coreproductpb,
  google,
} from "./protobuf-models/contract-ms-protobuf-models-es6";

export class ContractJSClient extends MicroService {
  static serviceName = "contract";

  /**
   * The use of the _definite assignment assertion_ (!) is required here
   * because we're neither initializing this property here, not is it apparent
   * to TS that is it being initialized in the constructor body.
   *
   * @see {@link https://www.typescriptlang.org/docs/handbook/release-notes/typescript-2-7.html#strict-class-initialization}
   */
  protected httpClient!: ProtobufHttpClient;

  constructor(baseUrl: string, httpClient: ProtobufHttpClient) {
    super(baseUrl, httpClient);
    this.httpClient = httpClient;
  }

  /**
   * @param requestData  Object with AsyncReqDetailsRequest fields to be created
   * @returns Object with AsyncReqDetailsResponse fields
   */
  getAsyncRequestObject = async (
    requestData: contractpb.IAsyncReqDetailsRequest,

    retryCount = 0,
    backoff = 0,
    timeout = 60000
  ): Promise<AxiosResponse<contractpb.IAsyncReqDetailsResponse>> => {
    const requestMessage = contractpb.AsyncReqDetailsRequest.create(
      requestData
    );
    const requestConfig: ProtobufAxiosRequestConfig<
      contractpb.AsyncReqDetailsRequest,
      contractpb.AsyncReqDetailsResponse
    > = {
      [REQUEST_CONFIG_NAMESPACE]: {
        requestProtobufModel: contractpb.AsyncReqDetailsRequest,
        responseProtobufModel: contractpb.AsyncReqDetailsResponse,
      },
    };
    return retry(
      () =>
        Promise.resolve(
          this.httpClient.getWithRequestDataAsUrlParam(
            this.makeServiceUrl("/v1/async-request-details"),
            requestMessage,
            requestConfig
          )
        ),
      { retries: retryCount, delay: backoff, timeout }
    );
  };

  /**
   * @param requestData  Object with AsyncReqProcessorUpdateRequest fields to be created
   * @returns Object with AsyncReqProcessorUpdateResponse fields
   */
  updateAsyncRequestObject = async (
    requestData: contractpb.IAsyncReqProcessorUpdateRequest,

    retryCount = 0,
    backoff = 0,
    timeout = 60000
  ): Promise<AxiosResponse<contractpb.IAsyncReqProcessorUpdateResponse>> => {
    const requestMessage = contractpb.AsyncReqProcessorUpdateRequest.create(
      requestData
    );
    const requestConfig: ProtobufAxiosRequestConfig<
      contractpb.AsyncReqProcessorUpdateRequest,
      contractpb.AsyncReqProcessorUpdateResponse
    > = {
      [REQUEST_CONFIG_NAMESPACE]: {
        requestProtobufModel: contractpb.AsyncReqProcessorUpdateRequest,
        responseProtobufModel: contractpb.AsyncReqProcessorUpdateResponse,
      },
    };
    return retry(
      () =>
        Promise.resolve(
          this.httpClient.patch(
            this.makeServiceUrl("/v1/async-request-details"),
            requestMessage,
            requestConfig
          )
        ),
      { retries: retryCount, delay: backoff, timeout }
    );
  };

  /**
   * @param requestData  Object with BillingDateChangeabilityRequest fields to be created
   * @returns Object with BillingDateChangeabilityResponse fields
   */
  getBillingDateChangeability = async (
    requestData: contractpb.IBillingDateChangeabilityRequest,

    retryCount = 0,
    backoff = 0,
    timeout = 60000
  ): Promise<AxiosResponse<contractpb.IBillingDateChangeabilityResponse>> => {
    const requestMessage = contractpb.BillingDateChangeabilityRequest.create(
      requestData
    );
    const requestConfig: ProtobufAxiosRequestConfig<
      contractpb.BillingDateChangeabilityRequest,
      contractpb.BillingDateChangeabilityResponse
    > = {
      [REQUEST_CONFIG_NAMESPACE]: {
        requestProtobufModel: contractpb.BillingDateChangeabilityRequest,
        responseProtobufModel: contractpb.BillingDateChangeabilityResponse,
      },
    };
    return retry(
      () =>
        Promise.resolve(
          this.httpClient.getWithRequestDataAsUrlParam(
            this.makeServiceUrl("/v1/billingdatechangeability"),
            requestMessage,
            requestConfig
          )
        ),
      { retries: retryCount, delay: backoff, timeout }
    );
  };

  /**
   * @param requestData  Object with GetCancellationCostReq fields to be created
   * @returns Object with GetContractCostsResp fields
   */
  getContractCancellationCosts = async (
    requestData: contractpb.IGetCancellationCostReq,

    retryCount = 0,
    backoff = 0,
    timeout = 60000
  ): Promise<AxiosResponse<contractpb.IGetContractCostsResp>> => {
    const requestMessage = contractpb.GetCancellationCostReq.create(
      requestData
    );
    const requestConfig: ProtobufAxiosRequestConfig<
      contractpb.GetCancellationCostReq,
      contractpb.GetContractCostsResp
    > = {
      [REQUEST_CONFIG_NAMESPACE]: {
        requestProtobufModel: contractpb.GetCancellationCostReq,
        responseProtobufModel: contractpb.GetContractCostsResp,
      },
    };
    return retry(
      () =>
        Promise.resolve(
          this.httpClient.getWithRequestDataAsUrlParam(
            this.makeServiceUrl("/v1/cancellation-costs"),
            requestMessage,
            requestConfig
          )
        ),
      { retries: retryCount, delay: backoff, timeout }
    );
  };

  /**
   * @param requestData  Object with UpdateContractRequest fields to be created
   * @param id A string path param
   * @returns Object with UpdateContractResponse fields
   */
  updateContractByID = async (
    requestData: contractpb.IUpdateContractRequest,
    id: string,
    retryCount = 0,
    backoff = 0,
    timeout = 60000
  ): Promise<AxiosResponse<contractpb.IUpdateContractResponse>> => {
    const requestMessage = contractpb.UpdateContractRequest.create(requestData);
    const requestConfig: ProtobufAxiosRequestConfig<
      contractpb.UpdateContractRequest,
      contractpb.UpdateContractResponse
    > = {
      [REQUEST_CONFIG_NAMESPACE]: {
        requestProtobufModel: contractpb.UpdateContractRequest,
        responseProtobufModel: contractpb.UpdateContractResponse,
      },
    };
    return retry(
      () =>
        Promise.resolve(
          this.httpClient.patch(
            this.makeServiceUrl(`/${id}`),
            requestMessage,
            requestConfig
          )
        ),
      { retries: retryCount, delay: backoff, timeout }
    );
  };

  /**
   * @param requestData  Object with DeleteContractRequest fields to be created
   * @param id A string path param
   * @returns Object with DeleteContractResponse fields
   */
  deleteContractByID = async (
    requestData: contractpb.IDeleteContractRequest,
    id: string,
    retryCount = 0,
    backoff = 0,
    timeout = 60000
  ): Promise<AxiosResponse<contractpb.IDeleteContractResponse>> => {
    const requestMessage = contractpb.DeleteContractRequest.create(requestData);
    const requestConfig: ProtobufAxiosRequestConfig<
      contractpb.DeleteContractRequest,
      contractpb.DeleteContractResponse
    > = {
      [REQUEST_CONFIG_NAMESPACE]: {
        requestProtobufModel: contractpb.DeleteContractRequest,
        responseProtobufModel: contractpb.DeleteContractResponse,
      },
    };
    return retry(
      () =>
        Promise.resolve(
          this.httpClient.deleteWithRequestDataAsUrlParam(
            this.makeServiceUrl(`/${id}`),
            requestMessage,
            requestConfig
          )
        ),
      { retries: retryCount, delay: backoff, timeout }
    );
  };

  /**
   * @param id A string path param
   * @param queryParams Query params: invoiceDetails,
   * @returns Object with ContractResponse fields
   */
  getContractByID = async (
    id: string,
    queryParams: { invoiceDetails?: string },
    retryCount = 0,
    backoff = 0,
    timeout = 60000
  ): Promise<AxiosResponse<contractpb.IContractResponse>> => {
    const requestConfig: ProtobufAxiosRequestConfig<contractpb.ContractResponse> = {
      params: queryParams,
      [REQUEST_CONFIG_NAMESPACE]: {
        requestProtobufModel: google.protobuf.Empty,
        responseProtobufModel: contractpb.ContractResponse,
      },
    };
    return retry(
      () =>
        Promise.resolve(
          this.httpClient.get(this.makeServiceUrl(`/${id}`), requestConfig)
        ),
      { retries: retryCount, delay: backoff, timeout }
    );
  };

  /**
   * @param requestData  Object with GetRenewalHierarchyRequest fields to be created
   * @returns Object with GetRenewalHierarchyResponse fields
   */
  getRenewalHierarchy = async (
    requestData: contractpb.IGetRenewalHierarchyRequest,

    retryCount = 0,
    backoff = 0,
    timeout = 60000
  ): Promise<AxiosResponse<contractpb.IGetRenewalHierarchyResponse>> => {
    const requestMessage = contractpb.GetRenewalHierarchyRequest.create(
      requestData
    );
    const requestConfig: ProtobufAxiosRequestConfig<
      contractpb.GetRenewalHierarchyRequest,
      contractpb.GetRenewalHierarchyResponse
    > = {
      [REQUEST_CONFIG_NAMESPACE]: {
        requestProtobufModel: contractpb.GetRenewalHierarchyRequest,
        responseProtobufModel: contractpb.GetRenewalHierarchyResponse,
      },
    };
    return retry(
      () =>
        Promise.resolve(
          this.httpClient.getWithRequestDataAsUrlParam(
            this.makeServiceUrl("/v1/renewal-hierarchy"),
            requestMessage,
            requestConfig
          )
        ),
      { retries: retryCount, delay: backoff, timeout }
    );
  };

  /**
   * @param requestData  Object with ContractCoverageRequest fields to be created
   * @returns Object with coreproductpb.GetProductsResponse fields
   */
  getContractCoverages = async (
    requestData: contractpb.IContractCoverageRequest,

    retryCount = 0,
    backoff = 0,
    timeout = 60000
  ): Promise<AxiosResponse<coreproductpb.IGetProductsResponse>> => {
    const requestMessage = contractpb.ContractCoverageRequest.create(
      requestData
    );
    const requestConfig: ProtobufAxiosRequestConfig<
      contractpb.ContractCoverageRequest,
      coreproductpb.GetProductsResponse
    > = {
      [REQUEST_CONFIG_NAMESPACE]: {
        requestProtobufModel: contractpb.ContractCoverageRequest,
        responseProtobufModel: coreproductpb.GetProductsResponse,
      },
    };
    return retry(
      () =>
        Promise.resolve(
          this.httpClient.getWithRequestDataAsUrlParam(
            this.makeServiceUrl("/v1/coverages"),
            requestMessage,
            requestConfig
          )
        ),
      { retries: retryCount, delay: backoff, timeout }
    );
  };

  /**
   * @param requestData  Object with UpdateCoveragesRequest fields to be created
   * @returns Object with UpdateCoveragesResponse fields
   */
  updateContractCoverages = async (
    requestData: contractpb.IUpdateCoveragesRequest,

    retryCount = 0,
    backoff = 0,
    timeout = 60000
  ): Promise<AxiosResponse<contractpb.IUpdateCoveragesResponse>> => {
    const requestMessage = contractpb.UpdateCoveragesRequest.create(
      requestData
    );
    const requestConfig: ProtobufAxiosRequestConfig<
      contractpb.UpdateCoveragesRequest,
      contractpb.UpdateCoveragesResponse
    > = {
      [REQUEST_CONFIG_NAMESPACE]: {
        requestProtobufModel: contractpb.UpdateCoveragesRequest,
        responseProtobufModel: contractpb.UpdateCoveragesResponse,
      },
    };
    return retry(
      () =>
        Promise.resolve(
          this.httpClient.post(
            this.makeServiceUrl("/v1/coverages"),
            requestMessage,
            requestConfig
          )
        ),
      { retries: retryCount, delay: backoff, timeout }
    );
  };

  /**
   * @param requestData  Object with DeleteContractCoverageRequest fields to be created
   * @returns Object with DeleteContractCoverageResponse fields
   */
  deleteContractCoverage = async (
    requestData: contractpb.IDeleteContractCoverageRequest,

    retryCount = 0,
    backoff = 0,
    timeout = 60000
  ): Promise<AxiosResponse<contractpb.IDeleteContractCoverageResponse>> => {
    const requestMessage = contractpb.DeleteContractCoverageRequest.create(
      requestData
    );
    const requestConfig: ProtobufAxiosRequestConfig<
      contractpb.DeleteContractCoverageRequest,
      contractpb.DeleteContractCoverageResponse
    > = {
      [REQUEST_CONFIG_NAMESPACE]: {
        requestProtobufModel: contractpb.DeleteContractCoverageRequest,
        responseProtobufModel: contractpb.DeleteContractCoverageResponse,
      },
    };
    return retry(
      () =>
        Promise.resolve(
          this.httpClient.deleteWithRequestDataAsUrlParam(
            this.makeServiceUrl("/v1/coverages"),
            requestMessage,
            requestConfig
          )
        ),
      { retries: retryCount, delay: backoff, timeout }
    );
  };

  /**
   * @param requestData  Object with ContractUnifiedSearchRequest fields to be created
   * @returns Object with ContractUnifiedSearchResponse fields
   */
  getContracts = async (
    requestData: contractpb.IContractUnifiedSearchRequest,

    retryCount = 0,
    backoff = 0,
    timeout = 60000
  ): Promise<AxiosResponse<contractpb.IContractUnifiedSearchResponse>> => {
    const requestMessage = contractpb.ContractUnifiedSearchRequest.create(
      requestData
    );
    const requestConfig: ProtobufAxiosRequestConfig<
      contractpb.ContractUnifiedSearchRequest,
      contractpb.ContractUnifiedSearchResponse
    > = {
      [REQUEST_CONFIG_NAMESPACE]: {
        requestProtobufModel: contractpb.ContractUnifiedSearchRequest,
        responseProtobufModel: contractpb.ContractUnifiedSearchResponse,
      },
    };
    return retry(
      () =>
        Promise.resolve(
          this.httpClient.post(
            this.makeServiceUrl("/v2/search"),
            requestMessage,
            requestConfig
          )
        ),
      { retries: retryCount, delay: backoff, timeout }
    );
  };

  /**
   * @param requestData  Object with UpdateContractStatusRequest fields to be created
   * @returns Object with UpdateContractStatusResponse fields
   */
  updateContractStatus = async (
    requestData: contractpb.IUpdateContractStatusRequest,

    retryCount = 0,
    backoff = 0,
    timeout = 60000
  ): Promise<AxiosResponse<contractpb.IUpdateContractStatusResponse>> => {
    const requestMessage = contractpb.UpdateContractStatusRequest.create(
      requestData
    );
    const requestConfig: ProtobufAxiosRequestConfig<
      contractpb.UpdateContractStatusRequest,
      contractpb.UpdateContractStatusResponse
    > = {
      [REQUEST_CONFIG_NAMESPACE]: {
        requestProtobufModel: contractpb.UpdateContractStatusRequest,
        responseProtobufModel: contractpb.UpdateContractStatusResponse,
      },
    };
    return retry(
      () =>
        Promise.resolve(
          this.httpClient.patch(
            this.makeServiceUrl("/v1/update-statuses"),
            requestMessage,
            requestConfig
          )
        ),
      { retries: retryCount, delay: backoff, timeout }
    );
  };

  /**
   * @param requestData  Object with ContractDownloadRequestV2 fields to be created
   * @returns Object with ContractDownloadResponseV2 fields
   */
  getContractDocument = async (
    requestData: contractpb.IContractDownloadRequestV2,

    retryCount = 0,
    backoff = 0,
    timeout = 60000
  ): Promise<AxiosResponse<contractpb.IContractDownloadResponseV2>> => {
    const requestMessage = contractpb.ContractDownloadRequestV2.create(
      requestData
    );
    const requestConfig: ProtobufAxiosRequestConfig<
      contractpb.ContractDownloadRequestV2,
      contractpb.ContractDownloadResponseV2
    > = {
      [REQUEST_CONFIG_NAMESPACE]: {
        requestProtobufModel: contractpb.ContractDownloadRequestV2,
        responseProtobufModel: contractpb.ContractDownloadResponseV2,
      },
    };
    return retry(
      () =>
        Promise.resolve(
          this.httpClient.getWithRequestDataAsUrlParam(
            this.makeServiceUrl("/v2/document"),
            requestMessage,
            requestConfig
          )
        ),
      { retries: retryCount, delay: backoff, timeout }
    );
  };

  /**
   * @param requestData  Object with GetContractsOnPvIDReq fields to be created
   * @returns Object with GetContractsOnPvIDResponse fields
   */
  getContractsOnPvID = async (
    requestData: contractpb.IGetContractsOnPvIDReq,

    retryCount = 0,
    backoff = 0,
    timeout = 60000
  ): Promise<AxiosResponse<contractpb.IGetContractsOnPvIDResponse>> => {
    const requestMessage = contractpb.GetContractsOnPvIDReq.create(requestData);
    const requestConfig: ProtobufAxiosRequestConfig<
      contractpb.GetContractsOnPvIDReq,
      contractpb.GetContractsOnPvIDResponse
    > = {
      [REQUEST_CONFIG_NAMESPACE]: {
        requestProtobufModel: contractpb.GetContractsOnPvIDReq,
        responseProtobufModel: contractpb.GetContractsOnPvIDResponse,
      },
    };
    return retry(
      () =>
        Promise.resolve(
          this.httpClient.getWithRequestDataAsUrlParam(
            this.makeServiceUrl("/v1/contracts-on-pvid"),
            requestMessage,
            requestConfig
          )
        ),
      { retries: retryCount, delay: backoff, timeout }
    );
  };

  /**
   * @param requestData  Object with ExistingCustomerContractsRequestV2 fields to be created
   * @returns Object with ExistingCustomerContractsResponseV2 fields
   */
  getExistingCutomerContractsV2 = async (
    requestData: contractpb.IExistingCustomerContractsRequestV2,

    retryCount = 0,
    backoff = 0,
    timeout = 60000
  ): Promise<
    AxiosResponse<contractpb.IExistingCustomerContractsResponseV2>
  > => {
    const requestMessage = contractpb.ExistingCustomerContractsRequestV2.create(
      requestData
    );
    const requestConfig: ProtobufAxiosRequestConfig<
      contractpb.ExistingCustomerContractsRequestV2,
      contractpb.ExistingCustomerContractsResponseV2
    > = {
      [REQUEST_CONFIG_NAMESPACE]: {
        requestProtobufModel: contractpb.ExistingCustomerContractsRequestV2,
        responseProtobufModel: contractpb.ExistingCustomerContractsResponseV2,
      },
    };
    return retry(
      () =>
        Promise.resolve(
          this.httpClient.getWithRequestDataAsUrlParam(
            this.makeServiceUrl("/v2/existing-customer-contracts"),
            requestMessage,
            requestConfig
          )
        ),
      { retries: retryCount, delay: backoff, timeout }
    );
  };

  /**
   * @param requestData  Object with ContractChangeTrackerRequest fields to be created
   * @returns Object with ContractChangeTrackerResponse fields
   */
  getChangeTracker = async (
    requestData: contractpb.IContractChangeTrackerRequest,

    retryCount = 0,
    backoff = 0,
    timeout = 60000
  ): Promise<AxiosResponse<contractpb.IContractChangeTrackerResponse>> => {
    const requestMessage = contractpb.ContractChangeTrackerRequest.create(
      requestData
    );
    const requestConfig: ProtobufAxiosRequestConfig<
      contractpb.ContractChangeTrackerRequest,
      contractpb.ContractChangeTrackerResponse
    > = {
      [REQUEST_CONFIG_NAMESPACE]: {
        requestProtobufModel: contractpb.ContractChangeTrackerRequest,
        responseProtobufModel: contractpb.ContractChangeTrackerResponse,
      },
    };
    return retry(
      () =>
        Promise.resolve(
          this.httpClient.getWithRequestDataAsUrlParam(
            this.makeServiceUrl("/v1/change-tracker"),
            requestMessage,
            requestConfig
          )
        ),
      { retries: retryCount, delay: backoff, timeout }
    );
  };

  /**
   * @param requestData  Object with UpdateContractPropertyRequestV2 fields to be created
   * @returns Object with UpdateContractPropertyResponseV2 fields
   */
  updateContractPropertyV2 = async (
    requestData: contractpb.IUpdateContractPropertyRequestV2,

    retryCount = 0,
    backoff = 0,
    timeout = 60000
  ): Promise<AxiosResponse<contractpb.IUpdateContractPropertyResponseV2>> => {
    const requestMessage = contractpb.UpdateContractPropertyRequestV2.create(
      requestData
    );
    const requestConfig: ProtobufAxiosRequestConfig<
      contractpb.UpdateContractPropertyRequestV2,
      contractpb.UpdateContractPropertyResponseV2
    > = {
      [REQUEST_CONFIG_NAMESPACE]: {
        requestProtobufModel: contractpb.UpdateContractPropertyRequestV2,
        responseProtobufModel: contractpb.UpdateContractPropertyResponseV2,
      },
    };
    return retry(
      () =>
        Promise.resolve(
          this.httpClient.put(
            this.makeServiceUrl("/v2/property"),
            requestMessage,
            requestConfig
          )
        ),
      { retries: retryCount, delay: backoff, timeout }
    );
  };

  /**
   * @param requestData  Object with CancelContractRequestV2 fields to be created
   * @returns Object with CancelContractResponseV2 fields
   */
  cancelContractV2 = async (
    requestData: contractpb.ICancelContractRequestV2,

    retryCount = 0,
    backoff = 0,
    timeout = 60000
  ): Promise<AxiosResponse<contractpb.ICancelContractResponseV2>> => {
    const requestMessage = contractpb.CancelContractRequestV2.create(
      requestData
    );
    const requestConfig: ProtobufAxiosRequestConfig<
      contractpb.CancelContractRequestV2,
      contractpb.CancelContractResponseV2
    > = {
      [REQUEST_CONFIG_NAMESPACE]: {
        requestProtobufModel: contractpb.CancelContractRequestV2,
        responseProtobufModel: contractpb.CancelContractResponseV2,
      },
    };
    return retry(
      () =>
        Promise.resolve(
          this.httpClient.post(
            this.makeServiceUrl("/v2/cancel"),
            requestMessage,
            requestConfig
          )
        ),
      { retries: retryCount, delay: backoff, timeout }
    );
  };

  /**
   * @param id A string path param
   * @returns Object with ContractProperty fields
   */
  getContractProperty = async (
    id: string,
    retryCount = 0,
    backoff = 0,
    timeout = 60000
  ): Promise<AxiosResponse<contractpb.IContractProperty>> => {
    const requestConfig: ProtobufAxiosRequestConfig<contractpb.ContractProperty> = {
      [REQUEST_CONFIG_NAMESPACE]: {
        requestProtobufModel: google.protobuf.Empty,
        responseProtobufModel: contractpb.ContractProperty,
      },
    };
    return retry(
      () =>
        Promise.resolve(
          this.httpClient.get(
            this.makeServiceUrl(`/${id}/property`),
            requestConfig
          )
        ),
      { retries: retryCount, delay: backoff, timeout }
    );
  };

  /**
   * @param requestData  Object with CreateContractRequest fields to be created
   * @returns Object with CreateContractResponse fields
   */
  createContract = async (
    requestData: contractpb.ICreateContractRequest,

    retryCount = 0,
    backoff = 0,
    timeout = 60000
  ): Promise<AxiosResponse<contractpb.ICreateContractResponse>> => {
    const requestMessage = contractpb.CreateContractRequest.create(requestData);
    const requestConfig: ProtobufAxiosRequestConfig<
      contractpb.CreateContractRequest,
      contractpb.CreateContractResponse
    > = {
      [REQUEST_CONFIG_NAMESPACE]: {
        requestProtobufModel: contractpb.CreateContractRequest,
        responseProtobufModel: contractpb.CreateContractResponse,
      },
    };
    return retry(
      () =>
        Promise.resolve(
          this.httpClient.post(
            this.makeServiceUrl("/create"),
            requestMessage,
            requestConfig
          )
        ),
      { retries: retryCount, delay: backoff, timeout }
    );
  };

  /**
   * @param requestData  Object with CreateContractRequest fields to be created
   * @returns Object with CreateContractResponse fields
   */
  createContractV2 = async (
    requestData: contractpb.ICreateContractRequest,

    retryCount = 0,
    backoff = 0,
    timeout = 60000
  ): Promise<AxiosResponse<contractpb.ICreateContractResponse>> => {
    const requestMessage = contractpb.CreateContractRequest.create(requestData);
    const requestConfig: ProtobufAxiosRequestConfig<
      contractpb.CreateContractRequest,
      contractpb.CreateContractResponse
    > = {
      [REQUEST_CONFIG_NAMESPACE]: {
        requestProtobufModel: contractpb.CreateContractRequest,
        responseProtobufModel: contractpb.CreateContractResponse,
      },
    };
    return retry(
      () =>
        Promise.resolve(
          this.httpClient.post(
            this.makeServiceUrl("/v2/create"),
            requestMessage,
            requestConfig
          )
        ),
      { retries: retryCount, delay: backoff, timeout }
    );
  };

  /**
   * @param requestData  Object with ContractDates fields to be created
   * @param id A string path param
   */
  updateContractDate = async (
    requestData: contractpb.IContractDates,
    id: string,
    retryCount = 0,
    backoff = 0,
    timeout = 60000
  ): Promise<AxiosResponse<google.protobuf.Empty>> => {
    const requestMessage = contractpb.ContractDates.create(requestData);

    const requestConfig: ProtobufAxiosRequestConfig<contractpb.ContractDates> = {
      [REQUEST_CONFIG_NAMESPACE]: {
        requestProtobufModel: contractpb.ContractDates,
        responseProtobufModel: google.protobuf.Empty,
      },
    };
    return retry(
      () =>
        Promise.resolve(
          this.httpClient.put(
            this.makeServiceUrl(`/${id}/dates`),
            requestMessage,
            requestConfig
          )
        ),
      { retries: retryCount, delay: backoff, timeout }
    );
  };

  /**
   * @param requestData  Object with GetContractInvoiceDayDetailsReq fields to be created
   * @returns Object with GetContractInvoiceDayDetailsResp fields
   */
  getContractInvoiceDayDetails = async (
    requestData: contractpb.IGetContractInvoiceDayDetailsReq,

    retryCount = 0,
    backoff = 0,
    timeout = 60000
  ): Promise<AxiosResponse<contractpb.IGetContractInvoiceDayDetailsResp>> => {
    const requestMessage = contractpb.GetContractInvoiceDayDetailsReq.create(
      requestData
    );
    const requestConfig: ProtobufAxiosRequestConfig<
      contractpb.GetContractInvoiceDayDetailsReq,
      contractpb.GetContractInvoiceDayDetailsResp
    > = {
      [REQUEST_CONFIG_NAMESPACE]: {
        requestProtobufModel: contractpb.GetContractInvoiceDayDetailsReq,
        responseProtobufModel: contractpb.GetContractInvoiceDayDetailsResp,
      },
    };
    return retry(
      () =>
        Promise.resolve(
          this.httpClient.getWithRequestDataAsUrlParam(
            this.makeServiceUrl("/v1/invoice-day-details"),
            requestMessage,
            requestConfig
          )
        ),
      { retries: retryCount, delay: backoff, timeout }
    );
  };
}
